import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import Header from '../Header';
import Sidebar from '../Sidebar';

const API_URL = process.env.REACT_APP_API_URL || '';

// Options arrays (matching the modal)
const productTypes = [
  'GAP Cover', 'Credit Shortfall', 'Car Hire', 'Excess Waiver',
  'Key Cover', 'Tyre and Rim Cover', 'Mobile Device Cover',
  'Laptop Cover', 'Furniture Cover', 'Appliance Cover', 'Other'
];

// Currency formatting helpers
const formatCurrencyInput = (value: string): string => {
  if (!value) return '';
  const numericValue = value.replace(/[^\d.]/g, '');
  const parts = numericValue.split('.');
  let wholePart = parts[0] || '';
  let decimalPart = parts[1] || '';
  if (decimalPart.length > 2) decimalPart = decimalPart.substring(0, 2);
  if (wholePart) wholePart = parseInt(wholePart, 10).toLocaleString('en-US');
  return decimalPart ? `${wholePart}.${decimalPart}` : wholePart;
};

const parseCurrencyInput = (value: string): number => {
  if (!value) return 0;
  return parseFloat(value.replace(/,/g, ''));
};

const AddValueAddedPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const fromPolicy = searchParams.get('fromPolicy') === 'true';
  const policyId = searchParams.get('policyId');  // ADD THIS
  const { quoteId } = useParams<{ quoteId: string }>();
  const clientId = location.state?.clientId || new URLSearchParams(location.search).get('clientId');
  const returnTo = location.state?.returnTo || new URLSearchParams(location.search).get('returnTo');
  
  // Determine if we're in master quote page mode (has returnTo URL) or create quote mode
  const isMasterQuoteMode = !!returnTo;
  
  console.log('📍 AddValueAddedPage mounted:', { 
    quoteId, 
    clientId, 
    policyId,  // ADD THIS
    fromPolicy,  // ADD THIS
    returnTo, 
    isMasterQuoteMode 
  });
  
  const [user, setUser] = useState<any>(null);
  const [isSidebarVisible, setIsSidebarVisible] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [localSaveMessage, setLocalSaveMessage] = useState('');
  
  // Form state
  const [productType, setProductType] = useState('');
  const [description, setDescription] = useState('');
  const [value, setValue] = useState('');
  const [premium, setPremium] = useState('');
  
  const [currencyDisplay, setCurrencyDisplay] = useState({
    value: '',
    premium: '',
  });
  
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  
  // Fetch user data
  useEffect(() => {
    const fetchUser = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API_URL}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setUser(response.data);
      } catch (error) {
        localStorage.removeItem('token');
        navigate('/login');
      }
    };
    fetchUser();
  }, [navigate]);
  
  // Fetch existing value added product if editing
  useEffect(() => {
    const editId = new URLSearchParams(location.search).get('editId');
    if (editId && quoteId) {
      setEditingId(editId);
      fetchValueAddedForEdit(editId);
    }
  }, [quoteId, location.search, policyId, fromPolicy]);
  
  const fetchValueAddedForEdit = async (id: string) => {
  console.log('🔍 fetchValueAddedForEdit called with ID:', id);
  
  try {
    const token = localStorage.getItem('token');
    const response = await axios.get(
      `${API_URL}/api/value-added/${id}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    
    const product = response.data.data || response.data;
    
    console.log('📦 Fetched Value Added product for edit:', JSON.stringify(product, null, 2));
    
    setProductType(product.product_type || product.productType || '');
    setDescription(product.description || '');
    setValue((product.value || 0).toString());
    setPremium((product.premium || 0).toString());
    
    setCurrencyDisplay({
      value: formatCurrencyInput((product.value || 0).toString()),
      premium: formatCurrencyInput((product.premium || 0).toString()),
    });
    
  } catch (error) {
    console.error('Error fetching value added product for edit:', error);
  }
};
  
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    
    switch (name) {
      case 'productType': setProductType(value); break;
      case 'description': setDescription(value); break;
      case 'value':
        setCurrencyDisplay(prev => ({ ...prev, value: formatCurrencyInput(value) }));
        setValue(parseCurrencyInput(value).toString());
        break;
      case 'premium':
        setCurrencyDisplay(prev => ({ ...prev, premium: formatCurrencyInput(value) }));
        setPremium(parseCurrencyInput(value).toString());
        break;
      default: break;
    }
    setErrors(prev => ({ ...prev, [name]: '' }));
  };
  
  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};
    if (!productType) newErrors.productType = 'Product Type is required';
    if (!description) newErrors.description = 'Description is required';
    if (!value || parseFloat(value) <= 0) newErrors.value = 'Value is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
    const handleSubmit = async (e: React.FormEvent) => {
  e.preventDefault();
  if (isSaving) return;
  if (!validateForm()) return;
  
  // Validate clientId
  if (!clientId || isNaN(parseInt(clientId))) {
    setErrors(prev => ({ ...prev, server: 'Valid client ID is required' }));
    return;
  }
  
  setIsSaving(true);
  setLocalSaveMessage('Saving value added product...');
  
  // Clean the quoteId - use undefined instead of null
  let cleanQuoteId: number | undefined = undefined;
  if (quoteId && quoteId !== 'null' && quoteId !== 'undefined' && !isNaN(parseInt(quoteId))) {
    cleanQuoteId = parseInt(quoteId);
  }
  
  // Helper function to convert to number
  const toNumber = (value: any): number => {
    if (value === undefined || value === null || value === '') return 0;
    const num = Number(value);
    return isNaN(num) ? 0 : num;
  };
  
  const submitData = {
  // ✅ Send both formats to be safe
  productType: productType,
  product_type: productType,
  description: description,
  value: toNumber(value),
  premium: toNumber(premium),
  client_id: parseInt(clientId),
  quote_id: cleanQuoteId,
  policy_id: policyId ? parseInt(policyId) : null,
  policyId: policyId ? parseInt(policyId) : null,
  fromPolicy: fromPolicy,
  from_policy: fromPolicy,
};
  
  console.log('📤 Submitting Value Added data:', JSON.stringify(submitData, null, 2));
  console.log('📤 Editing ID:', editingId);
  console.log('📤 Quote ID:', cleanQuoteId);
  console.log('📤 Client ID:', clientId);
  console.log('📤 Policy ID:', policyId);
  console.log('📤 From Policy:', fromPolicy);
  
  try {
    const token = localStorage.getItem('token');
    
    // SCENARIO 1: Has quote_id - Save to database
    // SCENARIO 1: Has quote_id - Save to database
if (cleanQuoteId) {
  console.log('💾 Saving to database with quote_id:', cleanQuoteId);
  
  if (editingId) {
    await axios.put(`${API_URL}/api/value-added/${editingId}`, submitData, {
      headers: { Authorization: `Bearer ${token}` }
    });
    setLocalSaveMessage('Value added product updated successfully!');
    
    // ✅ LOG ACTIVITY: Value Added Updated
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const performedBy = user?.username || user?.email || 'System';
      
      await axios.post(
        `${API_URL}/api/quote-activity-logs`,
        {
          quote_id: cleanQuoteId,
          action_type: 'product_updated',
          description: `Value added updated: ${productType || 'Value Added'}`,
          metadata: {
            productType: 'value_added',
            productId: editingId,
            performedBy: performedBy,
            productTypeName: productType || '',
            description: description || '',
            value: parseFloat(value) || 0,
            premium: parseFloat(premium) || 0,
            updatedAt: new Date().toISOString()
          }
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      console.log('✅ Activity logged: value_added_updated');
    } catch (logError) {
      console.error('❌ Failed to log value added update:', logError);
      // Don't fail the update if logging fails
    }
  } else {
    await axios.post(`${API_URL}/api/value-added`, submitData, {
      headers: { Authorization: `Bearer ${token}` }
    });
    setLocalSaveMessage('Value added product added successfully!');
    
    // ✅ LOG ACTIVITY: Value Added Added
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const performedBy = user?.username || user?.email || 'System';
      
      await axios.post(
        `${API_URL}/api/quote-activity-logs`,
        {
          quote_id: cleanQuoteId,
          action_type: 'product_added',
          description: `Value added added: ${productType || 'Value Added'}`,
          metadata: {
            productType: 'value_added',
            performedBy: performedBy,
            productTypeName: productType || '',
            description: description || '',
            value: parseFloat(value) || 0,
            premium: parseFloat(premium) || 0,
            addedAt: new Date().toISOString()
          }
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      console.log('✅ Activity logged: value_added_added');
    } catch (logError) {
      console.error('❌ Failed to log value added addition:', logError);
      // Don't fail the addition if logging fails
    }
  }
  
  // Navigate based on mode
const validClientId = clientId || new URLSearchParams(location.search).get('clientId');

if (!validClientId) {
  console.error('❌ No clientId available for navigation');
  setLocalSaveMessage('Error: Missing client ID');
  setIsSaving(false);
  return;
}

let targetUrl = '';

// If fromPolicy is true, navigate back to policy detail page with refresh flag
if (fromPolicy && policyId && returnTo) {
  const decodedReturnUrl = decodeURIComponent(returnTo);
  const separator = decodedReturnUrl.includes('?') ? '&' : '?';
  targetUrl = `${decodedReturnUrl}${separator}refreshProducts=true&productAdded=value_added&clientId=${validClientId}`;
  console.log('📍 Returning to policy detail page with refresh:', targetUrl);
} else if (isMasterQuoteMode && returnTo) {
  // Master quote mode - navigate back to master quote page
  const decodedReturnUrl = decodeURIComponent(returnTo);
  const separator = decodedReturnUrl.includes('?') ? '&' : '?';
  targetUrl = `${decodedReturnUrl}${separator}refreshProducts=true&productAdded=value_added&clientId=${validClientId}`;
  console.log('📍 Returning to master quote page:', targetUrl);
} else if (cleanQuoteId) {
  // Create quote mode but with quoteId - navigate to create quote page
  targetUrl = `/create-quote/${validClientId}?refreshProducts=true&activeTab=quotes`;
  console.log('📍 Returning to create quote page:', targetUrl);
} else {
  // Fallback
  targetUrl = `/create-quote/${validClientId}?refreshProducts=true`;
  console.log('📍 Fallback navigation:', targetUrl);
}

// ✅ Use both navigate and window.location for reliability
setTimeout(() => {
  // Try React Router navigation first
  navigate(targetUrl);
  
  // If the page doesn't navigate within 500ms, force a hard navigation
  setTimeout(() => {
    // Check if we're still on the same page by checking if the URL changed
    const currentPath = window.location.pathname;
    if (currentPath.includes('/add-value-added/')) {
      console.log('⚠️ React Router navigation didn\'t work, using window.location');
      window.location.href = targetUrl;
    }
  }, 500);
}, 1500);

return;
}
    
    // SCENARIO 2: No quote_id, but has policy_id - Save to localStorage with policy_id
    if (policyId && !isNaN(parseInt(policyId))) {
      console.log('💾 No quote ID, but policy_id exists - saving to localStorage with policy_id:', policyId);
      
      const storageKey = `policy_${policyId}_value_added`;
      const existingItems = JSON.parse(localStorage.getItem(storageKey) || '[]');
      
      // Check if we're editing an existing item
      if (editingId) {
        const index = existingItems.findIndex((item: any) => item.id === editingId);
        if (index !== -1) {
          existingItems[index] = {
            ...submitData,
            id: editingId,
            isTemp: true,
            savedAt: new Date().toISOString()
          };
        } else {
          // If editing ID not found, add as new
          existingItems.push({
            ...submitData,
            id: `temp-${Date.now()}`,
            isTemp: true,
            savedAt: new Date().toISOString()
          });
        }
      } else {
        existingItems.push({
          ...submitData,
          id: `temp-${Date.now()}`,
          isTemp: true,
          savedAt: new Date().toISOString()
        });
      }
      
      localStorage.setItem(storageKey, JSON.stringify(existingItems));
      
      setLocalSaveMessage('Product saved successfully!');
      
      // Navigate back to policy details with refresh flag
setTimeout(() => {
  if (fromPolicy && policyId && returnTo) {
    const decodedReturnUrl = decodeURIComponent(returnTo);
    const separator = decodedReturnUrl.includes('?') ? '&' : '?';
    const finalUrl = `${decodedReturnUrl}${separator}refreshProducts=true&productAdded=value_added&clientId=${clientId}`;
    console.log('📍 Returning to policy detail page with refresh:', finalUrl);
    navigate(finalUrl);
  } else {
    navigate(`/policy/${policyId}`, { state: { refresh: true } });
  }
}, 1500);
      
      return;
    }
    
    // SCENARIO 3: No quote_id AND no policy_id - Save to client draft
    console.log('💾 No quote ID and no policy ID - saving to client draft localStorage');
    
    const storageKey = `client_${clientId}_value_added_draft`;
    const existingDrafts = JSON.parse(localStorage.getItem(storageKey) || '[]');
    
    if (editingId) {
      const index = existingDrafts.findIndex((item: any) => item.id === editingId);
      if (index !== -1) {
        existingDrafts[index] = {
          ...submitData,
          id: editingId,
          isTemp: true,
          savedAt: new Date().toISOString()
        };
      } else {
        existingDrafts.push({
          ...submitData,
          id: `temp-${Date.now()}`,
          isTemp: true,
          savedAt: new Date().toISOString()
        });
      }
    } else {
      existingDrafts.push({
        ...submitData,
        id: `temp-${Date.now()}`,
        isTemp: true,
        savedAt: new Date().toISOString()
      });
    }
    
    localStorage.setItem(storageKey, JSON.stringify(existingDrafts));
    
    setLocalSaveMessage('Product saved to draft! It will be saved when you create the quote or policy.');
    
    setTimeout(() => {
      if (isMasterQuoteMode && returnTo) {
        const decodedReturnUrl = decodeURIComponent(returnTo);
        navigate(decodedReturnUrl);
      } else {
        navigate(`/create-quote/${clientId}`, { 
          state: { refreshProducts: true, hasDrafts: true }
        });
      }
    }, 1500);
    
  } catch (error: any) {
    console.error('Error saving value added product:', error);
    setLocalSaveMessage(error.response?.data?.message || 'Failed to save value added product');
    setIsSaving(false);
  }
};
  
  if (!user) return <div>Loading...</div>;
  
  return (
    <div className="min-h-screen bg-gray-100 flex">
      <Sidebar isVisible={isSidebarVisible} toggleSidebar={() => setIsSidebarVisible(!isSidebarVisible)} />
      <div className={`flex-1 flex flex-col transition-all duration-300 ${isSidebarVisible ? 'ml-64' : 'ml-0'}`}>
        <Header
          title={editingId ? 'Edit Value Added Product' : 'Add Value Added Product'}
          user={user}
          toggleSidebar={() => setIsSidebarVisible(!isSidebarVisible)}
          isSidebarVisible={isSidebarVisible}
          showClientNumber={true}
        />
        <div className="flex-1 p-6">
          <div className="max-w-2xl mx-auto">

            <button
  onClick={() => {
    // If fromPolicy is true, navigate back to policy detail page
    if (fromPolicy && policyId && returnTo) {
      // Return to policy detail page with refresh flag
      const decodedReturnUrl = decodeURIComponent(returnTo);
      const separator = decodedReturnUrl.includes('?') ? '&' : '?';
      const finalUrl = `${decodedReturnUrl}${separator}refreshProducts=true&productAdded=value_added&clientId=${clientId}`;
      console.log('📍 Back button - returning to policy detail page with refresh:', finalUrl);
      navigate(finalUrl);
    } else if (isMasterQuoteMode && returnTo) {
      // Go back to master quote page with refresh flag
      const decodedReturnUrl = decodeURIComponent(returnTo);
      const separator = decodedReturnUrl.includes('?') ? '&' : '?';
      const finalUrl = `${decodedReturnUrl}${separator}refreshProducts=true&productAdded=value_added&clientId=${clientId}`;
      console.log('📍 Back button - returning to master quote page with refresh:', finalUrl);
      navigate(finalUrl);
    } else {
      // Go back to create quote page
      navigate(`/create-quote/${clientId}`);
    }
  }}
  className="mb-4 px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600"
>
  ← {fromPolicy ? 'Back to Policy Details' : 'Back to Quote'}
</button>
            
            <div className="bg-white rounded-lg shadow-md p-8">
              <h2 className="text-2xl font-bold mb-6">
                {editingId ? 'Edit Value Added Product' : 'Add Value Added Product'}
              </h2>
              
              {localSaveMessage && (
                <div className={`mb-4 p-3 rounded ${localSaveMessage.includes('❌') ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
                  {localSaveMessage}
                </div>
              )}
              
              <form onSubmit={handleSubmit}>
                {/* Product Type */}
                <div className="mb-6">
                  <label className="block text-gray-700 font-bold mb-2 text-sm">Product Type</label>
                  <select
                    name="productType"
                    value={productType}
                    onChange={handleInputChange}
                    className={`shadow-sm border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm ${errors.productType ? 'border-red-500' : 'border-gray-300'}`}
                  >
                    <option value="" disabled>Select Product Type</option>
                    {productTypes.map(type => <option key={type} value={type}>{type}</option>)}
                  </select>
                  {errors.productType && <p className="text-red-500 text-xs mt-1">{errors.productType}</p>}
                </div>
                
                {/* Description */}
                <div className="mb-6">
                  <label className="block text-gray-700 font-bold mb-2 text-sm">Description</label>
                  <textarea
                    name="description"
                    value={description}
                    onChange={handleInputChange}
                    rows={4}
                    className={`shadow-sm border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm ${errors.description ? 'border-red-500' : 'border-gray-300'}`}
                    placeholder="Describe the product..."
                  />
                  {errors.description && <p className="text-red-500 text-xs mt-1">{errors.description}</p>}
                </div>
                
                {/* Value and Premium */}
                <div className="flex space-x-6 mb-6">
                  <div className="flex-1">
                    <label className="block text-gray-700 font-bold mb-2 text-sm">
                      Value <span className="font-bold italic text-gray-600">(Rand Value Only)</span>
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <span className="text-gray-500">R</span>
                      </div>
                      <input
                        type="text"
                        name="value"
                        value={currencyDisplay.value}
                        onChange={handleInputChange}
                        className={`shadow-sm border rounded w-full py-2 pl-8 pr-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm ${errors.value ? 'border-red-500' : 'border-gray-300'}`}
                        placeholder="0.00"
                      />
                    </div>
                    {errors.value && <p className="text-red-500 text-xs mt-1">{errors.value}</p>}
                  </div>
                  
                  <div className="flex-1">
                    <label className="block text-gray-700 font-bold mb-2 text-sm">Premium</label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <span className="text-gray-500">R</span>
                      </div>
                      <input
                        type="text"
                        name="premium"
                        value={currencyDisplay.premium}
                        onChange={handleInputChange}
                        className="shadow-sm border rounded w-full py-2 pl-8 pr-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300 text-sm"
                        placeholder="0.00"
                      />
                    </div>
                  </div>
                </div>
                
                {/* Buttons */}
                <div className="flex justify-end space-x-4 mt-6 pt-4 border-t">

                  <button
  type="button"
  onClick={() => {
    // If fromPolicy is true, navigate back to policy detail page
    if (fromPolicy && policyId && returnTo) {
      // Return to policy detail page with refresh flag
      const decodedReturnUrl = decodeURIComponent(returnTo);
      const separator = decodedReturnUrl.includes('?') ? '&' : '?';
      const finalUrl = `${decodedReturnUrl}${separator}refreshProducts=true&productAdded=value_added&clientId=${clientId}`;
      console.log('📍 Cancel button - returning to policy detail page with refresh:', finalUrl);
      navigate(finalUrl);
    } else if (isMasterQuoteMode && returnTo) {
      // Go back to master quote page with refresh flag
      const decodedReturnUrl = decodeURIComponent(returnTo);
      const separator = decodedReturnUrl.includes('?') ? '&' : '?';
      const finalUrl = `${decodedReturnUrl}${separator}refreshProducts=true&productAdded=value_added&clientId=${clientId}`;
      console.log('📍 Cancel button - returning to master quote page with refresh:', finalUrl);
      navigate(finalUrl);
    } else {
      navigate(`/create-quote/${clientId}`);
    }
  }}
  className="bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold py-2 px-4 rounded-lg"
>
  Cancel
</button>

                  <button
                    type="submit"
                    disabled={isSaving}
                    className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-lg disabled:bg-blue-300"
                  >
                    {isSaving ? 'Saving...' : (editingId ? 'Update Product' : 'Add Product')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddValueAddedPage;